import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, RefreshCw, Edit, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { swatidService } from '@/services';
import type { ApiSwatidSchedule } from '@/types/api';
import {
  SWATID_DAYS,
  SWATID_DAY_LABELS,
  SWATID_MAX_SLOTS_PER_DAY,
  SWATID_MODE_LABELS,
  type SwatidOperationMode,
} from '@/constants/swatid';
import { canAddSwatidSchedule } from '@/utils/swatidValidation';
import { useSwatidSchedules, useSwatidRelays, useSwatidRefresh } from '@/hooks/useSwatidController';
import {
  useScheduleVerification,
  buildVerifyToast,
} from '@/hooks/useScheduleVerification';
import { getSwatidRelayOptionLabel } from '@/utils/swatidRelay';
import { SwatidScheduleFormDialog } from './SwatidScheduleFormDialog';
import { ScheduleVerificationSummary } from '@/components/schedules/ScheduleVerificationSummary';
import {
  VerificationBadge,
  isVerificationIssue,
} from '@/components/schedules/VerificationBadge';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';

interface SwatidSchedulesSectionProps {
  controllerId: string;
  /** Programaciones editables en local y remota */
  editable?: boolean;
  currentMode?: SwatidOperationMode;
}

export function SwatidSchedulesSection({
  controllerId,
  editable = true,
  currentMode,
}: SwatidSchedulesSectionProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { refreshAll } = useSwatidRefresh(controllerId);
  const { data, isLoading, error } = useSwatidSchedules(controllerId, true);
  const { data: relays = [] } = useSwatidRelays(controllerId, true);
  const {
    verificationStatus,
    unavailable: verificationUnavailable,
    isLoading: verificationLoading,
    isVerifying,
    awaitingDevice,
    progress: verificationProgress,
    isLora,
    error: verificationError,
    verify,
    refresh: refreshVerification,
  } = useScheduleVerification(controllerId, {
    enabled: editable,
    isLora: false,
  });

  const [selectedRelay, setSelectedRelay] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [replaceSchedule, setReplaceSchedule] = useState<ApiSwatidSchedule | null>(null);
  const [addDay, setAddDay] = useState<string | null>(null);

  const schedules = data?.schedules ?? [];

  const relaySchedules = schedules.filter((s) => s.relayNumber === selectedRelay);

  const schedulesByDay = SWATID_DAYS.map((dayName) => ({
    dayName,
    items: relaySchedules.filter((s) => s.dayName === dayName),
  }));

  const deleteMutation = useMutation({
    mutationFn: async (scheduleId: string) => {
      const res = await swatidService.deleteSchedule(controllerId, scheduleId);
      if (!res.success) throw new Error(res.error?.message || 'Error al eliminar');
      return res.data;
    },
    onSuccess: async () => {
      await refreshAll(1000);
      await queryClient.invalidateQueries({ queryKey: ['schedule-verification', controllerId] });
      toast({ title: 'Programación eliminada' });
    },
    onError: (e: Error) => {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    },
  });

  /** SWATID: syncSchedules importa estado del dispositivo → plataforma */
  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await swatidService.syncSchedules(controllerId);
      if (!res.success) throw new Error(res.error?.message || 'Error al sincronizar');
      return res.data;
    },
    onSuccess: async () => {
      await refreshAll(1000);
      await queryClient.invalidateQueries({ queryKey: ['schedule-verification', controllerId] });
      toast({
        title: 'Importación iniciada',
        description:
          'Se están solicitado las programaciones del dispositivo. La plataforma se actualizará con la respuesta.',
      });
    },
    onError: (e: Error) => {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    },
  });

  const handleAdd = (day: string) => {
    const count = relaySchedules.filter((s) => s.dayName === day).length;
    if (!canAddSwatidSchedule(count)) {
      toast({
        title: 'Límite alcanzado',
        description: `Máximo ${SWATID_MAX_SLOTS_PER_DAY} programaciones por día y relé.`,
        variant: 'destructive',
      });
      return;
    }
    setReplaceSchedule(null);
    setAddDay(day);
    setFormOpen(true);
  };

  const handleEdit = (schedule: ApiSwatidSchedule) => {
    setReplaceSchedule(schedule);
    setAddDay(schedule.dayName);
    setFormOpen(true);
  };

  if (!editable) {
    return (
      <Alert>
        <AlertDescription>
          Las programaciones se gestionan en modo Local o Remota
          {currentMode && ` (modo actual: ${SWATID_MODE_LABELS[currentMode]})`}.
          En modo Manual no aplican programaciones.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <ScheduleVerificationSummary
        verification={verificationStatus}
        isLoading={verificationLoading}
        isVerifying={isVerifying}
        awaitingDevice={awaitingDevice}
        progress={verificationProgress}
        isLora={isLora}
        error={verificationError}
        unavailable={verificationUnavailable}
        onRefresh={refreshVerification}
        actions={{
          onImportFromDevice: async () => {
            await syncMutation.mutateAsync();
          },
        }}
        onVerify={async () => {
          try {
            const data = await verify(true);
            const msg = buildVerifyToast(data, false);
            toast({
              title: msg.title,
              description: msg.description,
              variant: msg.variant,
            });
            await refreshAll(500);
          } catch (e) {
            toast({
              title: 'Error al verificar',
              description: e instanceof Error ? e.message : 'Error desconocido',
              variant: 'destructive',
            });
          }
        }}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Relé:</span>
          <select
            value={selectedRelay}
            onChange={(e) => setSelectedRelay(Number(e.target.value))}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            {Array.from({ length: 16 }, (_, i) => i + 1).map((n) => {
              const relay = relays.find((r) => r.relayNumber === n);
              const label = relay
                ? getSwatidRelayOptionLabel(relay)
                : `Relé ${n}`;
              return (
                <option key={n} value={n}>
                  {label}
                </option>
              );
            })}
          </select>
          <Badge variant="outline">{relaySchedules.length} programaciones</Badge>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
        >
          {syncMutation.isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Importar del dispositivo
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertDescription>
            {error instanceof Error ? error.message : 'Error al cargar programaciones'}
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-4">
          {schedulesByDay.map(({ dayName, items }) => (
            <div key={dayName} className="rounded-lg border p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-sm">{SWATID_DAY_LABELS[dayName]}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => handleAdd(dayName)}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Añadir ({items.length}/{SWATID_MAX_SLOTS_PER_DAY})
                </Button>
              </div>
              {items.length === 0 ? (
                <p className="text-xs text-muted-foreground">Sin programaciones</p>
              ) : (
                <ul className="space-y-1">
                  {items.map((s) => (
                    <li
                      key={s.id}
                      className="flex items-center justify-between gap-2 text-sm py-1.5 px-2 rounded bg-muted/40"
                    >
                      <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <span>
                          {s.startTime} – {s.endTime}
                        </span>
                        <VerificationBadge
                          status={s.verificationStatus}
                          eventConfirmed={s.eventConfirmed}
                        />
                        {s.syncStatus && s.syncStatus !== 'synced' && (
                          <Badge variant="outline" className="text-xs">
                            Sync: {s.syncStatus}
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {isVerificationIssue(s.verificationStatus) && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => syncMutation.mutate()}
                            disabled={syncMutation.isPending}
                          >
                            Re-sincronizar
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handleEdit(s)}
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive"
                          onClick={() => {
                            if (confirm('¿Eliminar esta programación?')) {
                              deleteMutation.mutate(s.id);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      <SwatidScheduleFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        controllerId={controllerId}
        defaultRelayNumber={selectedRelay}
        defaultDay={addDay ?? 'lunes'}
        replaceSchedule={replaceSchedule}
      />
    </div>
  );
}
